// =============================================================================
// LP Express — Listicle template content model.
//
// A "Listicle" landing page is a long-scroll persuasion page with a FIXED
// structural skeleton (ported from the Papabear listicle design) whose copy is
// entirely brand/persona/product/angle-driven:
//
//   announcement marquee → hero (eyebrow / headline / subheadline / trust row /
//   offer summary card) → 10 numbered "reasons" (each with an optional typed
//   supporting visual) interleaved with a membership-framing block and three
//   CTA blocks → science/ingredients → trust & social proof montage →
//   membership explainer → 90-day roadmap → FAQ accordion → final CTA →
//   sticky mobile CTA bar.
//
// The whole listicle is stored as ONE structured blob at
// `generatedContent.listicle` (alongside `templateType: "listicle"`), exactly
// like the Quiz Funnel's `generatedContent.quiz`. It is generated in a single
// shot by a dedicated Sim Studio workflow (SIM_LP_LISTICLE_URL) and edited in
// place through the SAME generatedContent path-based `setText` ops the other
// templates use — e.g. ["listicle","reasons",0,"headline"].
//
// AI imagery lives SEPARATELY under `generatedContent.listicleImages` — an
// array of per-slot records ({ key, imageUrl, imageStatus, altText }) driven by
// the standard LP Express single-slot image pipeline (per-slot pending/ready/
// failed status, per-slot retry, startup resume). Product/CTA-card imagery is
// NEVER AI-generated: it always comes from the brand's real product photos.
// Prices, discounts, and savings figures are likewise DERIVED from the real
// product/offer/sale data at render time — the workflow's copy never invents
// numbers.
//
// This module is pure + dependency-free so it runs identically on the client
// (renderer + editor) and the server (generation/normalization).
//
// ─── Sim workflow contract (SIM_LP_LISTICLE_URL) ─────────────────────────────
//
// INPUT (top-level fields on the Start block — called with wrapInput=false,
// identical to the quiz workflow's envelope):
//   {
//     "brandInfo":      string  // serialized brand doctrine/profile
//     "personaInfo":    string  // serialized persona persuasion profile
//     "productInfo":    string  // serialized product/offer attributes
//     "narrativeAngle": string  // serialized narrative angle
//     "offerInfo":      string  // serialized offer/pricing/sale context
//     "feedback":       string  // optional free-form regeneration steering
//     "currentCopy":    string  // optional JSON of the CURRENT listicle blob
//                               // (set on regeneration so the workflow refines
//                               // instead of starting from scratch)
//   }
//
// OUTPUT: a single JSON object (may be ```json-fenced under output.content —
// the extractor strips fences) matching LpListicleContent below. snake_case
// aliases are accepted for every camelCase key. Recommended reason→visual
// sequence (the renderer accepts any order/types):
//   1 productGrid · 2 benefitList · 3 imageCards · 4 valueBreakdown ·
//   5 lifestylePair · 6 gallery · 7 comparison · 8 infoTable · 9 reviews ·
//   10 manifesto
// Derived visuals (productGrid, valueBreakdown, reviews) carry NO copy fields —
// they render from real products/receipt/reviews. Do NOT put prices or dollar
// amounts in any copy field.
// =============================================================================

export interface LpListicleHero {
  eyebrow: string; // e.g. "The 2026 Buyer's Guide"
  headline: string;
  subheadline: string;
  ctaText: string; // primary CTA label reused by the hero offer card
}

export interface LpListicleOfferCard {
  title: string; // offer-card heading, e.g. "The Complete Ritual Bundle"
  subtitle: string; // one-line supporting copy under the title
  retailLabel: string; // e.g. "Retail value"
  todayLabel: string; // e.g. "Today"
  badges: string[]; // up to 3 short trust badges (savings % badge is DERIVED)
  savingsLine: string; // qualitative savings/urgency line (NO dollar amounts)
}

// One of the ten numbered reasons. `visual` attaches an optional typed
// supporting block under the copy; unknown/absent types render copy-only.
export type LpListicleReasonVisual =
  | { type: "none" }
  // Real product cards (photo + name + price) derived from the run's products.
  | { type: "productGrid" }
  // Cream info box with titled benefit bullets.
  | { type: "benefitList"; items: { title: string; desc: string }[] }
  // Ingredient/component cards. Images come from the shared `science-N` AI
  // slots by position (card i uses science-(i+1)) so the science section and
  // these cards stay visually consistent (same source imagery as the export).
  | { type: "imageCards"; items: { name: string; desc: string }[] }
  // Line-item value breakdown derived from real products + receipt totals.
  | { type: "valueBreakdown" }
  // Two side-by-side lifestyle/process photos (AI slots lifestyle-1/2).
  | { type: "lifestylePair"; items: { label: string }[] }
  // Four-image montage (AI slots gallery-1..4).
  | { type: "gallery"; items: { caption: string }[] }
  // Two-column "them vs us" comparison.
  | {
      type: "comparison";
      leftLabel: string;
      leftItems: string[];
      rightLabel: string;
      rightItems: string[];
    }
  // Simple label/value rows (e.g. "What's in the box").
  | { type: "infoTable"; title: string; rows: { label: string; value: string }[] }
  // Real customer reviews derived from the brand's review data.
  | { type: "reviews" }
  // "What it is / what it isn't" manifesto columns.
  | {
      type: "manifesto";
      isLabel: string;
      isItems: string[];
      isntLabel: string;
      isntItems: string[];
    };

export interface LpListicleReason {
  headline: string;
  body: string;
  visual: LpListicleReasonVisual;
  // Editor-set: a hidden reason stays in the blob (so it can be un-hidden) but
  // is skipped by the renderer (and excluded from visible numbering).
  hidden?: boolean;
}

// "How the membership works" framing block (interleaved after reason 1).
export interface LpListicleMembership {
  header: string;
  headline: string;
  body: string;
  stats: { value: string; label: string; sub: string }[]; // up to 2
  bullets: string[]; // up to 4
}

// CTA block #1 — order-summary card (product rows/prices are DERIVED).
export interface LpListicleCtaSummary {
  header: string;
  savingsNote: string; // qualitative, no dollar amounts
  ctaText: string;
}

// CTA block #2 — freshness/benefit banner.
export interface LpListicleCtaFreshness {
  eyebrow: string;
  headline: string;
  bullets: string[]; // up to 4 short reassurance bullets
  kitLabel: string; // label on the mini product card
  ctaText: string;
}

// CTA block #3 — "for the record" checklist card.
export interface LpListicleCtaRecord {
  header: string;
  items: { title: string; sub: string }[]; // up to 5
  ctaText: string;
}

export interface LpListicleScience {
  eyebrow: string;
  headline: string;
  subheadline: string;
  // Ingredient/component rows; row i pairs with AI slot science-(i+1).
  rows: { name: string; desc: string }[]; // up to 5
}

export interface LpListicleTrustSection {
  eyebrow: string;
  headline: string;
  body: string;
  captions: string[]; // up to 4, pair with AI slots gallery-1..4
}

export interface LpListicleMembershipExplainer {
  eyebrow: string;
  headline: string;
  cardHeader: string;
  firstLabel: string; // e.g. "Your first delivery"
  renewalLabel: string; // e.g. "Renews quarterly — skip or cancel anytime"
  bullets: string[]; // up to 4
  ctaText: string;
}

export interface LpListicleRoadmap {
  eyebrow: string;
  headline: string;
  subheadline: string;
  phases: { period: string; items: string[] }[]; // up to 3 (e.g. Days 1–30 …)
}

export interface LpListicleFaq {
  eyebrow: string;
  headline: string;
  items: { q: string; a: string }[];
}

export interface LpListicleFinalCta {
  eyebrow: string;
  headline: string;
  subheadline: string;
  reassurance: { q: string; a: string }[]; // up to 2 reassurance cards
  productName: string; // display name on the final product card
  productMeta: string; // one-line meta under the name
  badges: string[]; // up to 3
  ctaText: string;
  finePrint: string;
}

export interface LpListicleSticky {
  ctaText: string;
  noteLine: string; // short line next to the sticky CTA (no dollar amounts)
}

export interface LpListicleContent {
  marqueeItems: string[]; // scrolling announcement items (up to 6)
  hero: LpListicleHero;
  trustItems: string[]; // hero trust row labels (up to 5)
  offerCard: LpListicleOfferCard;
  trustBullets: string[]; // short bullets repeated under CTAs (up to 4)
  reasons: LpListicleReason[]; // up to 10
  membership: LpListicleMembership;
  ctaSummary: LpListicleCtaSummary;
  ctaFreshness: LpListicleCtaFreshness;
  ctaRecord: LpListicleCtaRecord;
  science: LpListicleScience;
  trustSection: LpListicleTrustSection;
  membershipExplainer: LpListicleMembershipExplainer;
  roadmap: LpListicleRoadmap;
  faq: LpListicleFaq;
  finalCta: LpListicleFinalCta;
  sticky: LpListicleSticky;
}

// ── AI image slots ───────────────────────────────────────────────────────────
// The listicle's AI-generated imagery pool, stored under
// `generatedContent.listicleImages` as [{ key, imageUrl, imageStatus, altText }]
// and generated per-slot through the standard LP Express single-slot image
// pipeline. The variantHint steers each slot's Sim image-prompt call.
export const LP_LISTICLE_IMAGE_SLOTS: { key: string; variantHint: string }[] = [
  {
    key: "hero",
    variantHint:
      "Hero lifestyle scene: the product in a warm, aspirational real-life setting that matches the brand's world; editorial, photographic, no text overlays.",
  },
  {
    key: "lifestyle-1",
    variantHint:
      "Process/craft photo #1: hands making, preparing, or using the product up close; warm natural light, authentic and unstaged.",
  },
  {
    key: "lifestyle-2",
    variantHint:
      "Process/craft photo #2: a complementary in-use or preparation moment (different angle/scene than photo #1); warm natural light.",
  },
  {
    key: "gallery-1",
    variantHint:
      "Place/people montage photo #1 (wide): the brand's world — where it's made, sourced, or enjoyed; documentary style.",
  },
  {
    key: "gallery-2",
    variantHint:
      "Place/people montage photo #2 (tall): a person authentically engaging with the product or its setting; documentary style.",
  },
  {
    key: "gallery-3",
    variantHint:
      "Place/people montage photo #3: a detail shot from the brand's world (materials, tools, environment); documentary style.",
  },
  {
    key: "gallery-4",
    variantHint:
      "Place/people montage photo #4: another authentic scene from the brand's world, distinct from the other montage photos.",
  },
  {
    key: "science-1",
    variantHint:
      "Ingredient/component macro shot #1: the product's first key ingredient or component, isolated on a clean warm background.",
  },
  {
    key: "science-2",
    variantHint:
      "Ingredient/component macro shot #2: the product's second key ingredient or component, isolated on a clean warm background.",
  },
  {
    key: "science-3",
    variantHint:
      "Ingredient/component macro shot #3: the product's third key ingredient or component, isolated on a clean warm background.",
  },
  {
    key: "science-4",
    variantHint:
      "Ingredient/component macro shot #4: the product's fourth key ingredient or component, isolated on a clean warm background.",
  },
  {
    key: "science-5",
    variantHint:
      "Ingredient/component macro shot #5: the product's fifth key ingredient or component, isolated on a clean warm background.",
  },
];

// ── Normalization ────────────────────────────────────────────────────────────

function asObj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : fallback;
}

function strArray(v: unknown, cap = 20): string[] {
  return Array.isArray(v)
    ? v.filter((x) => typeof x === "string" && x.trim()).slice(0, cap)
    : [];
}

function titleDescArray(
  v: unknown,
  keys: { a: string[]; b: string[] },
  cap = 8,
): { title: string; desc: string }[] {
  return (Array.isArray(v) ? v : [])
    .map((it) => {
      const o = asObj(it);
      const pick = (names: string[]) => {
        for (const n of names) {
          const s = str(o[n]);
          if (s) return s;
        }
        return "";
      };
      return { title: pick(keys.a), desc: pick(keys.b) };
    })
    .filter((it) => it.title || it.desc)
    .slice(0, cap);
}

function normalizeReasonVisual(raw: unknown): LpListicleReasonVisual {
  const o = asObj(raw);
  const type = str(o.type).trim();
  switch (type) {
    case "productGrid":
    case "product_grid":
      return { type: "productGrid" };
    case "valueBreakdown":
    case "value_breakdown":
      return { type: "valueBreakdown" };
    case "reviews":
      return { type: "reviews" };
    case "benefitList":
    case "benefit_list":
      return {
        type: "benefitList",
        items: titleDescArray(o.items, { a: ["title"], b: ["desc", "description"] }),
      };
    case "imageCards":
    case "image_cards":
      return {
        type: "imageCards",
        items: titleDescArray(o.items, {
          a: ["name", "title"],
          b: ["desc", "description"],
        }, 5).map((it) => ({ name: it.title, desc: it.desc })),
      };
    case "lifestylePair":
    case "lifestyle_pair":
      return {
        type: "lifestylePair",
        items: (Array.isArray(o.items) ? o.items : [])
          .map((it) => ({ label: str(asObj(it).label ?? asObj(it).caption) }))
          .slice(0, 2),
      };
    case "gallery":
      return {
        type: "gallery",
        items: (Array.isArray(o.items) ? o.items : [])
          .map((it) => ({ caption: str(asObj(it).caption ?? asObj(it).label) }))
          .slice(0, 4),
      };
    case "comparison":
      return {
        type: "comparison",
        leftLabel: str(o.leftLabel ?? o.left_label),
        leftItems: strArray(o.leftItems ?? o.left_items, 6),
        rightLabel: str(o.rightLabel ?? o.right_label),
        rightItems: strArray(o.rightItems ?? o.right_items, 6),
      };
    case "infoTable":
    case "info_table":
      return {
        type: "infoTable",
        title: str(o.title),
        rows: (Array.isArray(o.rows) ? o.rows : [])
          .map((r) => ({ label: str(asObj(r).label), value: str(asObj(r).value) }))
          .filter((r) => r.label || r.value)
          .slice(0, 8),
      };
    case "manifesto":
      return {
        type: "manifesto",
        isLabel: str(o.isLabel ?? o.is_label),
        isItems: strArray(o.isItems ?? o.is_items, 6),
        isntLabel: str(o.isntLabel ?? o.isnt_label),
        isntItems: strArray(o.isntItems ?? o.isnt_items, 6),
      };
    default:
      return { type: "none" };
  }
}

// Normalize a raw listicle payload (e.g. from the Sim workflow, which may use
// snake_case) into the canonical LpListicleContent shape. Missing fields become
// empty strings/arrays so the renderer/editor never crash — the caller decides
// whether an empty result counts as a generation failure (isLpListicleContentUsable).
export function normalizeLpListicleContent(raw: unknown): LpListicleContent {
  const o = asObj(raw);

  const heroSrc = asObj(o.hero);
  const hero: LpListicleHero = {
    eyebrow: str(heroSrc.eyebrow),
    headline: str(heroSrc.headline),
    subheadline: str(heroSrc.subheadline ?? heroSrc.subhead),
    ctaText: str(heroSrc.ctaText ?? heroSrc.cta_text) || "Claim the offer",
  };

  const ocSrc = asObj(o.offerCard ?? o.offer_card);
  const offerCard: LpListicleOfferCard = {
    title: str(ocSrc.title),
    subtitle: str(ocSrc.subtitle),
    retailLabel: str(ocSrc.retailLabel ?? ocSrc.retail_label) || "Retail value",
    todayLabel: str(ocSrc.todayLabel ?? ocSrc.today_label) || "Today",
    badges: strArray(ocSrc.badges, 3),
    savingsLine: str(ocSrc.savingsLine ?? ocSrc.savings_line),
  };

  const reasons: LpListicleReason[] = (Array.isArray(o.reasons) ? o.reasons : [])
    .map((r) => {
      const ro = asObj(r);
      const reason: LpListicleReason = {
        headline: str(ro.headline ?? ro.title),
        body: str(ro.body ?? ro.description),
        visual: normalizeReasonVisual(ro.visual),
      };
      if (Boolean(ro.hidden)) reason.hidden = true;
      return reason;
    })
    .filter((r) => r.headline || r.body)
    .slice(0, 10);

  const memSrc = asObj(o.membership);
  const membership: LpListicleMembership = {
    header: str(memSrc.header),
    headline: str(memSrc.headline),
    body: str(memSrc.body),
    stats: (Array.isArray(memSrc.stats) ? memSrc.stats : [])
      .map((s) => ({
        value: str(asObj(s).value),
        label: str(asObj(s).label),
        sub: str(asObj(s).sub),
      }))
      .filter((s) => s.value || s.label)
      .slice(0, 2),
    bullets: strArray(memSrc.bullets, 4),
  };

  const csSrc = asObj(o.ctaSummary ?? o.cta_summary);
  const ctaSummary: LpListicleCtaSummary = {
    header: str(csSrc.header),
    savingsNote: str(csSrc.savingsNote ?? csSrc.savings_note),
    ctaText: str(csSrc.ctaText ?? csSrc.cta_text) || hero.ctaText,
  };

  const cfSrc = asObj(o.ctaFreshness ?? o.cta_freshness);
  const ctaFreshness: LpListicleCtaFreshness = {
    eyebrow: str(cfSrc.eyebrow),
    headline: str(cfSrc.headline),
    bullets: strArray(cfSrc.bullets, 4),
    kitLabel: str(cfSrc.kitLabel ?? cfSrc.kit_label),
    ctaText: str(cfSrc.ctaText ?? cfSrc.cta_text) || hero.ctaText,
  };

  const crSrc = asObj(o.ctaRecord ?? o.cta_record);
  const ctaRecord: LpListicleCtaRecord = {
    header: str(crSrc.header),
    items: titleDescArray(crSrc.items, { a: ["title"], b: ["sub", "desc"] }, 5).map(
      (it) => ({ title: it.title, sub: it.desc }),
    ),
    ctaText: str(crSrc.ctaText ?? crSrc.cta_text) || hero.ctaText,
  };

  const sciSrc = asObj(o.science);
  const science: LpListicleScience = {
    eyebrow: str(sciSrc.eyebrow),
    headline: str(sciSrc.headline),
    subheadline: str(sciSrc.subheadline ?? sciSrc.subhead),
    rows: titleDescArray(sciSrc.rows, { a: ["name", "title"], b: ["desc", "description"] }, 5).map(
      (it) => ({ name: it.title, desc: it.desc }),
    ),
  };

  const tsSrc = asObj(o.trustSection ?? o.trust_section);
  const trustSection: LpListicleTrustSection = {
    eyebrow: str(tsSrc.eyebrow),
    headline: str(tsSrc.headline),
    body: str(tsSrc.body),
    captions: strArray(tsSrc.captions, 4),
  };

  const meSrc = asObj(o.membershipExplainer ?? o.membership_explainer);
  const membershipExplainer: LpListicleMembershipExplainer = {
    eyebrow: str(meSrc.eyebrow),
    headline: str(meSrc.headline),
    cardHeader: str(meSrc.cardHeader ?? meSrc.card_header),
    firstLabel: str(meSrc.firstLabel ?? meSrc.first_label),
    renewalLabel: str(meSrc.renewalLabel ?? meSrc.renewal_label),
    bullets: strArray(meSrc.bullets, 4),
    ctaText: str(meSrc.ctaText ?? meSrc.cta_text) || hero.ctaText,
  };

  const rmSrc = asObj(o.roadmap);
  const roadmap: LpListicleRoadmap = {
    eyebrow: str(rmSrc.eyebrow),
    headline: str(rmSrc.headline),
    subheadline: str(rmSrc.subheadline ?? rmSrc.subhead),
    phases: (Array.isArray(rmSrc.phases) ? rmSrc.phases : [])
      .map((p) => ({
        period: str(asObj(p).period ?? asObj(p).label),
        items: strArray(asObj(p).items, 5),
      }))
      .filter((p) => p.period || p.items.length)
      .slice(0, 3),
  };

  const faqSrc = asObj(o.faq);
  const faq: LpListicleFaq = {
    eyebrow: str(faqSrc.eyebrow),
    headline: str(faqSrc.headline),
    items: (Array.isArray(faqSrc.items) ? faqSrc.items : [])
      .map((it) => ({
        q: str(asObj(it).q ?? asObj(it).question),
        a: str(asObj(it).a ?? asObj(it).answer),
      }))
      .filter((it) => it.q)
      .slice(0, 10),
  };

  const fcSrc = asObj(o.finalCta ?? o.final_cta);
  const finalCta: LpListicleFinalCta = {
    eyebrow: str(fcSrc.eyebrow),
    headline: str(fcSrc.headline),
    subheadline: str(fcSrc.subheadline ?? fcSrc.subhead),
    reassurance: (Array.isArray(fcSrc.reassurance) ? fcSrc.reassurance : [])
      .map((it) => ({
        q: str(asObj(it).q ?? asObj(it).question),
        a: str(asObj(it).a ?? asObj(it).answer),
      }))
      .filter((it) => it.q || it.a)
      .slice(0, 2),
    productName: str(fcSrc.productName ?? fcSrc.product_name),
    productMeta: str(fcSrc.productMeta ?? fcSrc.product_meta),
    badges: strArray(fcSrc.badges, 3),
    ctaText: str(fcSrc.ctaText ?? fcSrc.cta_text) || hero.ctaText,
    finePrint: str(fcSrc.finePrint ?? fcSrc.fine_print),
  };

  const stSrc = asObj(o.sticky ?? o.sticky_bar ?? o.stickyBar);
  const sticky: LpListicleSticky = {
    ctaText: str(stSrc.ctaText ?? stSrc.cta_text) || hero.ctaText,
    noteLine: str(stSrc.noteLine ?? stSrc.note_line),
  };

  return {
    marqueeItems: strArray(o.marqueeItems ?? o.marquee_items, 6),
    hero,
    trustItems: strArray(o.trustItems ?? o.trust_items, 5),
    offerCard,
    trustBullets: strArray(o.trustBullets ?? o.trust_bullets, 4),
    reasons,
    membership,
    ctaSummary,
    ctaFreshness,
    ctaRecord,
    science,
    trustSection,
    membershipExplainer,
    roadmap,
    faq,
    finalCta,
    sticky,
  };
}

// True when a normalized listicle carries enough structure to render a real
// page (a hero headline + at least three substantive reasons). Used by the
// generator to reject an empty Sim response instead of persisting a blank page.
export function isLpListicleContentUsable(
  listicle: LpListicleContent | null | undefined,
): boolean {
  if (!listicle) return false;
  const hasHero = !!listicle.hero?.headline;
  const usableReasons = Array.isArray(listicle.reasons)
    ? listicle.reasons.filter((r) => r.headline && r.body).length
    : 0;
  return hasHero && usableReasons >= 3;
}
